import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  createDiResource,
  deleteDiResource,
  listDiResources,
  testDiResource,
  updateDiResource,
  type CreateDiResourceRequest,
  type DiResourceResponse,
  type ProviderTestResponse,
  type UpdateDiResourceRequest,
} from '../api';

export function useDiResources(): UseQueryResult<DiResourceResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.diResources(),
    queryFn: ({ signal }) => listDiResources(signal),
    staleTime: 30_000,
  });
}

export function useCreateDiResource() {
  const qc = useQueryClient();
  return useMutation<DiResourceResponse, Error, CreateDiResourceRequest>({
    mutationFn: (req) => createDiResource(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.diResources() });
    },
  });
}

export function useUpdateDiResource() {
  const qc = useQueryClient();
  return useMutation<DiResourceResponse, Error, { id: string; request: UpdateDiResourceRequest }>({
    mutationFn: ({ id, request }) => updateDiResource(id, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.diResources() });
    },
  });
}

export function useDeleteDiResource() {
  const qc = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: (id) => deleteDiResource(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.diResources() });
    },
  });
}

export function useTestDiResource() {
  return useMutation<ProviderTestResponse, Error, string>({
    mutationFn: (id) => testDiResource(id),
  });
}
